import SceneObject from "../../../lib/services/scene/sceneobject";
import CharacterBuilder from "../../../lib/services/render/builders/character_builder";
import { CharacterRuntime } from "../../../lib/services/render/builders/character_runtime";
import CharacterBuilderGpu from "../../../lib/services/compute/character_builder_gpu";
import AnimationPool from "../../../lib/services/compute/animation_pool";
import AnimationLibraryPool from "../../../lib/services/compute/animation_library_pool";
import AnimationComputePipeline from "../../../lib/services/compute/pipelines/animation_compute_pipeline";
import AnimationControllerGpu from "../../../lib/services/animation/animation_controller_gpu";
import Camera from "../../../lib/services/scene/camera";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import { PuppyClips }from "./puppy_clips"
import BlobShadow from "../../../lib/services/scene/blob_shadow";

export default class Puppy extends SceneObject 
{
    builder: CharacterBuilder;
    runtime: CharacterRuntime | null = null;
    // GPU animation infrastructure.
    builder_gpu: CharacterBuilderGpu | null = null;
    library_pool: AnimationLibraryPool | null = null;
    animation_pool: AnimationPool | null = null;
    animation_compute_pipeline: AnimationComputePipeline | null = null;
    animation_controller_gpu: AnimationControllerGpu | null = null;

  //cheap shadow
    shadow: BlobShadow;

    constructor(key: string) 
    {
        super(key);
        this.builder = new CharacterBuilder();
        this.shadow = new BlobShadow("puppy_shadow", 1);
        this.add_child(this.shadow);
    }

    //called once to load and setup everything this object needs to  render and perform gameplay
    async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> 
  {
    this.runtime = await this.builder.build(
      {
        key: this.key,
        shader_name: "papercraft_skinned",
        mesh_name: "puppy",
        texture_name: "puppy",
        animation_name: "puppy_rm",
        default_loop_index: PuppyClips.Idle_1,
      },
      device,
      format,
      shader_service,
      resource_service
    );

    //animation runs on gpu for performance 
     await this.build_gpu_animation(device, shader_service);
     await super.build(device, format, shader_service, resource_service);

     // initial spawn location
    
    this.transform.set_scale(1, 1, 1);
    this.transform.set_position(0.5132098197937012, 0, 5.9);
    this.transform.set_rotation(0, 0, 0);
    this.transform.update_matrix();

    this.shadow.update_shadow(
      this.transform.position[0],
      this.transform.position[1],
      this.transform.position[2]
    );

    //initial animation
    if(this.animation_controller_gpu)
    {
        this.animation_controller_gpu.playLoopByIndex(
        PuppyClips.Sitting_loop_1); 
     }

   
  }

  //helper function 
  private async build_gpu_animation(
    device: GPUDevice,
    shader_service: ShaderService
  ): Promise<void> {
    if (!this.runtime) return;

    const model = this.runtime.model;

    if (!model.meshes.length) {
      throw new Error("Puppy model contains no mesh primitives");
    }

    const primitive = model.meshes[0];
    const skin_index = primitive.skinIndex;
    if (skin_index === undefined) {
      throw new Error("Puppy primitive is not associated with a skin");
    }

    const mesh_node_index = primitive.nodeIndex;
    this.builder_gpu = new CharacterBuilderGpu(this.key);
    await this.builder_gpu.build(
      device,
      model,
      this.runtime.animation_library,
      skin_index,
      mesh_node_index
    );

    this.library_pool = new AnimationLibraryPool();
    this.library_pool.initialize();
    this.library_pool.populate(this.builder_gpu);

    this.animation_pool = new AnimationPool();
    this.animation_pool.initialize(
      device,
      this.key,
      this.builder_gpu.node_count,
      this.builder_gpu.joint_count
    );

     this.animation_compute_pipeline = new AnimationComputePipeline(this.key);
     await this.animation_compute_pipeline.build(device, shader_service);

     this.animation_controller_gpu = new AnimationControllerGpu(
      this.key,
      this.runtime.animation_library,
      this.builder_gpu,
      this.library_pool,
      this.animation_pool,
      this.animation_compute_pipeline
    );

    //todo show how to attach to a bone

 

 }

 //called every frame 
 tick(delta_time: number): void 
 {
    if (!this.runtime) return;

    //animation controller needs to be ticked
    if (this.animation_controller_gpu)
    {
        this.animation_controller_gpu.tick(delta_time);
    }

    //update the matrix
     this.transform.update_matrix();

     //update the shadow
     this.shadow.update_shadow(
      this.transform.position[0],
      this.transform.position[1],
      this.transform.position[2]
    );

     super.tick(delta_time);
 }

    //compute function is called by the RenderService during compute pass
    override compute(device: GPUDevice, pass: GPUComputePassEncoder, encoder:GPUCommandEncoder): void
    {
        //need to encode the animation controllers pipeline
        if (this.animation_controller_gpu) {
        this.animation_controller_gpu.encode_compute(device, pass);
        }

        super.compute(device, pass,encoder);
    }

    //prepare is called before draw to update any matrices or gpu buffers
    override prepare(device: GPUDevice, camera: Camera): void {
    if (!this.runtime) return;

    if (this.animation_controller_gpu) {
      this.runtime.skinned_renderpipeline.prepare(
        device,
        camera,
        this.transform,
        this.animation_controller_gpu
      );
    }

    super.prepare(device, camera);
  }

  //draw is called by RenderService for us to encode drawing commands for this object
  override draw(pass: GPURenderPassEncoder): void {
    this.runtime?.skinned_renderpipeline.draw(pass);
    super.draw(pass);
  }

  //called after draw for any cleanup or needing to know the render has been submitted
  override finish_draw(): void 
  {
    this.runtime?.skinned_renderpipeline.finish_draw();
    super.finish_draw();
  }

   ui_message(cmd: string, msg: string): void {
    super.ui_message(cmd, msg);
  }

}